import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  getOppositionStatusDetails,
  saveOppositionClientConfirmation,
  type OppositionConflictingMarkItem,
} from "api/opposition";

export const OPPOSITION_CLIENT_CONFIRMATION_FORM_ID =
  "opposition-client-confirmation-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function YesNoToggle({
  value,
  onChange,
  disabled,
}: {
  value: boolean | null;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(true)}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors disabled:opacity-60 ${
          value === true
            ? "bg-[#222222] text-white"
            : "bg-white border border-[#EEEEEE] text-[#222222] hover:bg-[#F7F7F7]"
        }`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-shrink-0"
        >
          <circle
            cx="8"
            cy="8"
            r="7.5"
            fill={value === true ? "white" : "none"}
            stroke={value === true ? "white" : "#D1D5DB"}
          />
          {value === true && (
            <path
              d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
              stroke="#222222"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
        YES
      </button>

      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(false)}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-[6px] text-xs font-medium transition-colors disabled:opacity-60 ${
          value === false
            ? "bg-[#222222] text-white"
            : "bg-white border border-[#EEEEEE] text-[#222222] hover:bg-[#F7F7F7]"
        }`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="flex-shrink-0"
        >
          <circle
            cx="8"
            cy="8"
            r="7.5"
            fill={value === false ? "white" : "none"}
            stroke={value === false ? "white" : "#D1D5DB"}
          />
          {value === false && (
            <path
              d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
              stroke="#222222"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
        NO
      </button>
    </div>
  );
}

export default function OppositionClientConfirmationView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [conflictingMarks, setConflictingMarks] = useState<
    OppositionConflictingMarkItem[]
  >([]);
  const [decisions, setDecisions] = useState<Record<string, boolean | null>>({});

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setConflictingMarks([]);
      setDecisions({});
      return;
    }
    let cancelled = false;
    setLoading(true);

    getOppositionStatusDetails(leadId)
      .then((statusData) => {
        if (cancelled) return;
        const marks = statusData.conflictingMarks ?? [];
        setConflictingMarks(marks);
        // Prefill from saved per-mark decisions
        const prefilled: Record<string, boolean | null> = {};
        marks.forEach((m) => {
          prefilled[m.id] =
            m.proceedWithOpposition !== undefined
              ? m.proceedWithOpposition
              : null;
        });
        setDecisions(prefilled);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error
              ? err.message
              : "Failed to load confirmation details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handleDecisionChange = (markId: string, value: boolean) => {
    setDecisions((prev) => ({ ...prev, [markId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;

    if (conflictingMarks.length === 0) {
      toast.error(
        "No conflicting applications found. Please complete Step 3 first."
      );
      return;
    }

    const missing = conflictingMarks.filter(
      (m) => decisions[m.id] === null || decisions[m.id] === undefined
    );
    if (missing.length > 0) {
      toast.error(
        `Please select YES or NO for all conflicting applications (${missing.length} pending).`
      );
      return;
    }

    setSaving(true);
    try {
      await saveOppositionClientConfirmation(leadId, {
        confirmations: conflictingMarks.map((m) => ({
          conflictingMarkId: m.id,
          proceed: decisions[m.id] as boolean,
        })),
      });
      toast.success("Client confirmation saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save client confirmation"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  if (conflictingMarks.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No conflicting applications found. Please complete Step 3 (Status
          Details) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CLIENT_CONFIRMATION_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-3 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Table header */}
      <div className="hidden md:grid grid-cols-[1fr_auto] gap-4 px-3 pb-1 border-b border-[#EEEEEE]">
        <span className="text-[11px] font-medium text-[#6D6D6D] uppercase tracking-wide">
          List of Conflicting Applications
        </span>
        <span className="text-[11px] font-medium text-[#6D6D6D] uppercase tracking-wide">
          Confirmation
        </span>
      </div>

      {/* Per-application rows */}
      {conflictingMarks.map((mark, idx) => (
        <div
          key={mark.id}
          className="flex flex-col md:grid md:grid-cols-[1fr_auto] gap-3 md:gap-4 items-start md:items-center px-3 py-3 rounded-[6px] border border-[#EEEEEE] bg-white"
        >
          {/* Application details */}
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[12px] font-medium text-[#222222] leading-5">
              {idx + 1}.{" "}
              {mark.conflictingMark ? (
                <>
                  {mark.conflictingMark}
                  {mark.applicationNo && (
                    <span className="font-normal text-[#6D6D6D]">
                      {" "}( {mark.applicationNo} )
                    </span>
                  )}
                </>
              ) : (
                <span className="text-[#6D6D6D]">—</span>
              )}
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              {mark.classOfMark != null && (
                <span className="text-[11px] text-[#6D6D6D] leading-5">
                  Class:{" "}
                  <span className="text-[#222222]">{mark.classOfMark}</span>
                </span>
              )}
              {mark.nameOfProprietor && (
                <span className="text-[11px] text-[#6D6D6D] leading-5">
                  Proprietor:{" "}
                  <span className="text-[#222222]">{mark.nameOfProprietor}</span>
                </span>
              )}
            </div>
          </div>

          {/* YES / NO */}
          <YesNoToggle
            value={decisions[mark.id] ?? null}
            onChange={(v) => handleDecisionChange(mark.id, v)}
            disabled={saving}
          />
        </div>
      ))}
    </form>
  );
}
